import type {
  EstimateItem,
  EstimateItemMaterial,
  EstimateTotals,
  ItemInput,
  MaterialLineInput,
} from '@shared/types';

type CalculatedMaterialFields = Omit<
  EstimateItemMaterial,
  | 'id'
  | 'materialId'
  | 'materialNameSnapshot'
  | 'unitId'
  | 'unitNameSnapshot'
  | 'unitCategorySnapshot'
>;

type CalculatedItemFields = Pick<
  EstimateItem,
  'basePaise' | 'discountedPaise' | 'gstPaise' | 'itemTotalPaise'
>;

// One material line: base = qty × rate; discount applied first, then GST on the
// discounted amount (matches PRD §8 ordering, applied at the line level so each
// material can carry its own tax rate).
export function calculateMaterialLine(input: MaterialLineInput): CalculatedMaterialFields {
  validateLineInput(input);
  const basePaise = roundToInteger(input.quantity * input.ratePaise);
  const discountedPaise = roundToInteger(basePaise * (1 - input.discountPercent / 100));
  const gstPaise = roundToInteger((discountedPaise * input.gstPercent) / 100);
  const lineTotalPaise = discountedPaise + gstPaise;
  return {
    quantity: input.quantity,
    ratePaise: input.ratePaise,
    discountPercent: input.discountPercent,
    gstPercent: input.gstPercent,
    basePaise,
    discountedPaise,
    gstPaise,
    lineTotalPaise,
  };
}

// Item totals are pure sums of its material-line totals. There is no separate
// item-level discount or GST — add a "Making charge" or "Wastage" material row
// in the catalog if you need those as separately-taxed components.
export function calculateItem(input: ItemInput): CalculatedItemFields {
  if (input.materials.length === 0) throw new Error('item must have at least one material line');
  let basePaise = 0;
  let discountedPaise = 0;
  let gstPaise = 0;
  let itemTotalPaise = 0;
  for (const line of input.materials) {
    const calc = calculateMaterialLine(line);
    basePaise += calc.basePaise;
    discountedPaise += calc.discountedPaise;
    gstPaise += calc.gstPaise;
    itemTotalPaise += calc.lineTotalPaise;
  }
  return { basePaise, discountedPaise, gstPaise, itemTotalPaise };
}

// Per-line breakdown derived from a SAVED estimate. Splits the subtotal into
// its pre-tax and tax components so the totals card can show the user how
// much of the bill is tax (a regulatory concern under DPDP/GST audits — the
// customer is owed a clear breakdown). Inputs are flat material lines, not
// EstimateItem rows, because the saved-estimate shape exposes basePaise /
// discountedPaise / gstPaise on each material directly.
export type SubtotalBreakdown = {
  // Sum of base (qty × rate) before any per-line discount. Useful when the
  // user wants to see "what was the gross before discounts".
  baseBeforeDiscountPaise: number;
  // Sum of post-line-discount, pre-tax amounts. This is the customer-facing
  // "Subtotal (without taxes)".
  subtotalWithoutTaxPaise: number;
  // Sum of GST charged across all lines. The customer-facing "Total tax".
  totalTaxPaise: number;
};

export function summarizeSavedLines(
  lines: ReadonlyArray<{ basePaise: number; discountedPaise: number; gstPaise: number }>,
): SubtotalBreakdown {
  let baseBeforeDiscountPaise = 0;
  let subtotalWithoutTaxPaise = 0;
  let totalTaxPaise = 0;
  for (const line of lines) {
    baseBeforeDiscountPaise += line.basePaise;
    subtotalWithoutTaxPaise += line.discountedPaise;
    totalTaxPaise += line.gstPaise;
  }
  return { baseBeforeDiscountPaise, subtotalWithoutTaxPaise, totalTaxPaise };
}

export function calculateEstimate(
  items: ReadonlyArray<EstimateItem>,
  estimateDiscountPercent: number,
): EstimateTotals {
  validatePercent(estimateDiscountPercent, 'estimate discount');

  const subtotalPaise = items.reduce((sum, item) => sum + item.itemTotalPaise, 0);
  const estimateDiscountPaise = roundToInteger((subtotalPaise * estimateDiscountPercent) / 100);
  const totalPaise = subtotalPaise - estimateDiscountPaise;

  return {
    subtotalPaise,
    estimateDiscountPercent,
    estimateDiscountPaise,
    totalPaise,
  };
}

function validateLineInput(input: MaterialLineInput): void {
  assertFinite(input.quantity, 'quantity');
  assertFinite(input.ratePaise, 'rate');
  assertFinite(input.discountPercent, 'discount');
  assertFinite(input.gstPercent, 'gst');
  if (input.quantity < 0) throw new Error('quantity must be non-negative');
  if (input.ratePaise < 0) throw new Error('rate must be non-negative');
  if (!Number.isInteger(input.ratePaise)) throw new Error('rate must be an integer (paise)');
  validatePercent(input.discountPercent, 'discount');
  validatePercent(input.gstPercent, 'gst');
}

function validatePercent(value: number, label: string): void {
  assertFinite(value, label);
  if (value < 0) throw new Error(`${label} must be non-negative`);
  if (value > 100) throw new Error(`${label} must not exceed 100`);
}

function assertFinite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`${label} must be a finite number`);
}

// Money rounding. Inputs validated non-negative upstream, so Math.round (toward +∞)
// is equivalent to half-away-from-zero here. If negative inputs become valid (refunds),
// switch to: Math.sign(v) * Math.round(Math.abs(v)).
function roundToInteger(value: number): number {
  return Math.round(value);
}
